/**
 * InquiryDetailPage — I-2-2. 문의 상세
 *
 * - 제목 · 카테고리 배지 · 상태 배지 · 등록일
 * - 질문 영역: 본문 텍스트
 * - 답변 영역: HTML 렌더링 또는 대기 안내
 */
import { useNavigate, useParams } from 'react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button } from '@indipia/ui-core';
import { fetchInquiryDetail } from '../api/inquiries';
import type { InquiryDetail } from '../api/inquiries';
import { fetchImageBlobUrl } from '../api/files';
import { ImageLightbox } from '../components/ImageLightbox';

// D-09 §15.5 Badge status 매핑 — 자체 배합 대신 표준 상태 토큰쌍을 쓴다
function statusOf(status: string | null): 'success' | 'warning' | 'info' {
  switch (status) {
    case 'DONE': return 'success';
    case 'IN_PROGRESS': return 'warning';
    default: return 'info';
  }
}

function statusLabel(q: InquiryDetail): string {
  if (q.statusName) return q.statusName;
  switch (q.inquiryStatus) {
    case 'DONE': return '✓ 답변완료';
    case 'IN_PROGRESS': return '● 처리중';
    default: return '○ 접수됨';
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}.${pad(dt.getMonth() + 1)}.${pad(dt.getDate())}`;
}

export function InquiryDetailPage() {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const id = Number(inquiryId);

  const { data: q, isLoading, isError, refetch } = useQuery({
    queryKey: ['inquiry-detail', id],
    queryFn: () => fetchInquiryDetail(id),
    enabled: !isNaN(id),
  });

  const [blobUrls, setBlobUrls] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const fileKey = (q?.files ?? []).map((f) => f.fileId).join(',');

  useEffect(() => {
    const files = q?.files;
    if (!files?.length) { setBlobUrls([]); return; }

    let cancelled = false;
    Promise.all(files.map((f) => fetchImageBlobUrl(f.fileUrl))).then((urls) => {
      if (cancelled) { urls.forEach(URL.revokeObjectURL); return; }
      setBlobUrls(urls);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  useEffect(() => {
    return () => { blobUrls.forEach(URL.revokeObjectURL); };
  }, [blobUrls]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-6 py-6 pb-16">

        {/* 뒤로가기 */}
        <Button
          variant="ghost"
          onClick={() => navigate('/cs/inquiry')}
          className="!mb-5 !flex !items-center !gap-1.5 !text-xs !text-text-muted"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          1:1 문의 목록으로
        </Button>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <svg className="animate-spin text-text-muted" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="mb-3 text-sm text-text-muted">문의를 불러오지 못했어요</p>
            <Button variant="ghost" onClick={() => void refetch()}>다시 시도</Button>
          </div>
        )}

        {q && (
          <div
            className="max-w-2xl overflow-hidden rounded-xl border"
            style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}
          >
            {/* 헤더 */}
            <div className="border-b px-6 py-5" style={{ borderColor: 'var(--shell-border)' }}>
              <h1 className="mb-2.5 text-base font-bold leading-snug text-text">{q.caseTitle}</h1>
              <div className="flex flex-wrap items-center gap-2">
                {q.categoryName && <Badge status="info">{q.categoryName}</Badge>}
                <Badge status={statusOf(q.inquiryStatus)}>{statusLabel(q)}</Badge>
                <span className="text-xs text-text-muted">{formatDate(q.createdAt)} 등록</span>
              </div>
            </div>

            {/* 질문 영역 */}
            <div className="border-b px-6 py-5" style={{ borderColor: 'var(--shell-border)' }}>
              <p className="mb-3 text-xs font-bold uppercase tracking-widest text-text-muted">질문</p>
              <p className="text-sm leading-relaxed text-text">{q.caseContent ?? ''}</p>

              {/* 첨부 이미지 */}
              {blobUrls.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {blobUrls.map((src, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      className="h-20 w-20 overflow-hidden rounded-xl border border-border transition-opacity hover:opacity-90"
                    >
                      <img
                        src={src}
                        alt={`첨부이미지 ${i + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 답변 영역 */}
            <div className="px-6 py-5">
              <div className="mb-3 flex items-center gap-1.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
                  <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                </svg>
                <p className="text-sm font-extrabold text-text">답변</p>
                {q.answered && q.answeredDtm && (
                  <span className="text-xs text-text-muted">{formatDate(q.answeredDtm)}</span>
                )}
              </div>
              <div
                className="rounded-xl p-4"
                style={{
                  background: q.answered
                    ? 'color-mix(in srgb, var(--gt-primary) 5%, var(--shell-surface))'
                    : 'var(--shell-surface)',
                  border: '1px solid var(--shell-border)',
                }}
              >
                {q.answered && q.caseAnswer ? (
                  <div
                    className="text-sm leading-relaxed text-text"
                    dangerouslySetInnerHTML={{ __html: q.caseAnswer }}
                  />
                ) : (
                  <p className="text-sm leading-relaxed text-text-muted">
                    아직 답변이 등록되지 않았어요. 답변이 등록되면 알림으로 알려드릴게요.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={blobUrls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
